import React, { useState } from 'react';
import { NavLink, useLocation, Link as RouterLink, Outlet } from 'react-router-dom';
import { auth } from '../../firebase';
import { useAuth } from '../../context/AuthContext';
import { useColorMode } from '../../context/ThemeContext';
import { Box, Toolbar, List, ListItem, ListItemButton, ListItemIcon, ListItemText, useTheme, Avatar, IconButton, Menu, MenuItem, useMediaQuery, CircularProgress, Collapse, Typography, AppBar, Drawer } from '@mui/material';
import DashboardIcon from '@mui/icons-material/Dashboard';
import MenuIcon from '@mui/icons-material/Menu';
import PeopleIcon from '@mui/icons-material/People';
import DevicesOtherIcon from '@mui/icons-material/DevicesOther';
import AssessmentIcon from '@mui/icons-material/Assessment';
import ListAltIcon from '@mui/icons-material/ListAlt';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import MiscellaneousServicesIcon from '@mui/icons-material/MiscellaneousServices';
import BusinessIcon from '@mui/icons-material/Business'; // YENİ
import MedicalServicesIcon from '@mui/icons-material/MedicalServices'; // YENİ
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import SmsIcon from '@mui/icons-material/Sms';
import NotificationBell from './NotificationBell';
import DashboardCustomizeIcon from '@mui/icons-material/DashboardCustomize';
import SettingsIcon from '@mui/icons-material/Settings';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import InventoryIcon from '@mui/icons-material/Inventory';
import HotelIcon from '@mui/icons-material/Hotel';
import BuildCircleIcon from '@mui/icons-material/BuildCircle';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import Brightness4Icon from '@mui/icons-material/Brightness4';
import Brightness7Icon from '@mui/icons-material/Brightness7';
import VoiceAssistantUI from './VoiceAssistantUI';

const drawerWidth = 280;

const SubMenuItem = ({ item, isMobile, handleDrawerToggle }) => {
    const location = useLocation();
    const isSelected = location.pathname === item.path || location.pathname.startsWith(item.path);
    const theme = useTheme();

    return (
        <ListItemButton
            component={NavLink}
            to={item.path}
            onClick={isMobile ? handleDrawerToggle : undefined}
            selected={isSelected}
            sx={{ pl: 4, '&.Mui-selected, &.Mui-selected:hover': { backgroundColor: theme.palette.action.selected } }}
        >
            <ListItemIcon sx={{ minWidth: '40px' }}>{item.icon}</ListItemIcon>
            <ListItemText primary={item.text} />
        </ListItemButton>
    );
};

const MainLayout = () => {
    const { currentUser } = useAuth();
    const { toggleColorMode, mode } = useColorMode();
    const location = useLocation();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('lg'));
    const [mobileOpen, setMobileOpen] = useState(false);
    const [anchorEl, setAnchorEl] = useState(null);
    const [openSubMenu, setOpenSubMenu] = useState('');

    // YENİ: Push Notification Token Yönetimi
    React.useEffect(() => {
        if (currentUser && currentUser.uid) {
            import('../../pwa/pushNotifications').then(({ requestForToken }) => {
                requestForToken().then(token => {
                    if (token) {
                        import('firebase/firestore').then(({ doc, updateDoc }) => {
                            import('../../firebase').then(({ db }) => {
                                const userRef = doc(db, 'users', currentUser.uid);
                                updateDoc(userRef, { fcmToken: token }).catch(err => {
                                    // Eğer hata permission ise veya alan yoksa set ile dene, ama genelde user var.
                                    console.log("Token kaydedilemedi:", err);
                                });
                            });
                        });
                    }
                });
            });
        }
    }, [currentUser]);

    if (!currentUser || !currentUser.role) {
        return (<Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}><CircularProgress /></Box>);
    }

    const handleDrawerToggle = () => setMobileOpen(!mobileOpen);
    const handleLogout = () => auth.signOut();
    const handleMenuOpen = (event) => setAnchorEl(event.currentTarget);
    const handleMenuClose = () => setAnchorEl(null);
    const handleSubMenuClick = (menuName) => {
        setOpenSubMenu(openSubMenu === menuName ? '' : menuName);
    };

    const allMenuItems = [
        // 1. Ana Menü (Direkt Erişim)
        { text: 'Ana Sayfa', path: '/dashboard', icon: <DashboardIcon /> },
        { text: 'Servis Kayıtları', path: '/servis-kayitlari', icon: <ListAltIcon /> },
        { text: 'Ürün Satış Kayıtları', path: '/urun-satis-kayitlari', icon: <ShoppingCartIcon /> },

        // 2. Servis İşlemleri (Grup)
        {
            text: 'Servis İşlemleri',
            icon: <MedicalServicesIcon />,
            name: 'servisIslemleri',
            subItems: [
                { text: 'Servis Panosu', path: '/servis-panosu', icon: <DashboardCustomizeIcon /> },
                { text: 'Randevular', path: '/randevular', icon: <EventAvailableIcon /> },
                { text: 'Ön Görüşme', path: '/on-gorusme', icon: <SupportAgentIcon /> },
                { text: 'Periyodik Bakım', path: '/periyodik-bakim', icon: <BuildCircleIcon />, roles: ['Yönetici', 'Ofis Personeli'] },
                { text: 'Uyku Takip', path: '/uyku-takip', icon: <HotelIcon />, roles: ['Yönetici', 'Ofis Personeli'] },
            ]
        },

        // 3. Müşteriler & Cihazlar (Grup)
        {
            text: 'Müşteriler & Cihazlar',
            icon: <PeopleIcon />,
            name: 'musteriler',
            subItems: [
                { text: 'Müşteri Listesi', path: '/musteriler', icon: <ListAltIcon /> },
                { text: 'Cihaz Listesi', path: '/cihazlar', icon: <DevicesOtherIcon /> },
                { text: 'SGK Zimmetli Cihazlar', path: '/sgk-cihazlari', icon: <MedicalServicesIcon /> },
            ]
        },

        // 4. Finans & Stok (Grup)
        {
            text: 'Finans & Stok',
            icon: <AccountBalanceWalletIcon />,
            name: 'finansStok',
            roles: ['Yönetici', 'Ofis Personeli'],
            subItems: [
                { text: 'Cari Hesaplar', path: '/cari-hesaplar', icon: <AccountBalanceWalletIcon /> },
                { text: 'Stok Takip', path: '/stok-takip', icon: <InventoryIcon /> },
            ]
        },

        // 5. Raporlar
        { text: 'Raporlar', path: '/raporlar', icon: <AssessmentIcon />, roles: ['Yönetici'] },

        // 6. Ayarlar (Grup)
        {
            text: 'Ayarlar',
            icon: <SettingsIcon />,
            name: 'ayarlar',
            roles: ['Yönetici'],
            subItems: [
                { text: 'Hizmet & Malzeme', path: '/hizmetler', icon: <MiscellaneousServicesIcon /> },
                { text: 'Marka & Model', path: '/ayarlar/marka-model', icon: <DevicesOtherIcon /> },
                { text: 'Firma Bilgileri', path: '/ayarlar/firma-bilgileri', icon: <BusinessIcon /> },
                { text: 'SMS Yönetimi', path: '/sms-yonetimi', icon: <SmsIcon /> },
                { text: 'Yapay Zeka', path: '/ayarlar/yapay-zeka', icon: <SmartToyIcon /> },
                { text: 'Kullanıcı Yönetimi', path: '/kullanici-yonetimi', icon: <PeopleIcon /> },
            ]
        },
    ];

    // Menü öğelerini role göre filtrele - rolePermissions kullanarak
    const menuItems = allMenuItems.filter(item => {
        // Eğer menü öğesinin özel rolleri tanımlıysa, kullanıcı o rollerde değilse gösterme
        if (item.roles && !item.roles.includes(currentUser.role)) return false;
        // Tüm diğer menü öğelerini göster (rolePermissions.js'deki izinlere göre)
        return true;
    });

    const drawerContent = (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            <Toolbar component={RouterLink} to="/dashboard" sx={{ display: 'flex', justifyContent: 'center', p: 2, height: '100px', textDecoration: 'none' }}>
                <img src="/reslife-logo.png" alt="ResLife Logo" style={{ height: '100%', width: '100%', objectFit: 'contain' }} />
            </Toolbar>
            {/* Divider removed for cleaner look, or kept subtle */}
            <List sx={{ p: 1, flexGrow: 1, overflowY: 'auto' }}>
                {menuItems.map((item) => (
                    item.subItems ? (
                        <React.Fragment key={item.text}>
                            <ListItemButton onClick={() => handleSubMenuClick(item.name)} sx={{ borderRadius: 2, mb: 0.5 }}>
                                <ListItemIcon sx={{ minWidth: '40px' }}>{item.icon}</ListItemIcon>
                                <ListItemText primary={item.text} />
                                {openSubMenu === item.name ? <ExpandLess /> : <ExpandMore />}
                            </ListItemButton>
                            <Collapse in={openSubMenu === item.name} timeout="auto" unmountOnExit>
                                <List component="div" disablePadding>
                                    {item.subItems.map(sub => <SubMenuItem key={sub.text} item={sub} isMobile={isMobile} handleDrawerToggle={handleDrawerToggle} />)}
                                </List>
                            </Collapse>
                        </React.Fragment>
                    ) : (
                        <ListItem key={item.text} disablePadding>
                            <ListItemButton component={NavLink} to={item.path} onClick={isMobile ? handleDrawerToggle : undefined}
                                selected={location.pathname === item.path}
                                sx={{ '&.Mui-selected, &.Mui-selected:hover': { backgroundColor: theme.palette.action.selected }, borderRadius: 2, margin: '4px 0' }}
                            >
                                <ListItemIcon sx={{ minWidth: '40px' }}>{item.icon}</ListItemIcon>
                                <ListItemText primary={item.text} />
                            </ListItemButton>
                        </ListItem>
                    )
                ))}
            </List>

            {/* Bottom Controls Section */}
            <Box sx={{ p: 2, borderTop: `1px solid ${theme.palette.divider}` }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-around', alignItems: 'center', mb: 2 }}>
                    <IconButton onClick={toggleColorMode} color="inherit">
                        {mode === 'dark' ? <Brightness7Icon /> : <Brightness4Icon />}
                    </IconButton>
                    <VoiceAssistantUI />
                    <NotificationBell />
                </Box>

                {/* User Profile / Logout */}
                {currentUser && (
                    <ListItemButton onClick={handleMenuOpen} sx={{ borderRadius: 2, bgcolor: 'action.hover' }}>
                        <ListItemIcon sx={{ minWidth: 40 }}>
                            <Avatar sx={{ bgcolor: 'secondary.main', width: 32, height: 32 }}>
                                {currentUser.email.substring(0, 1).toUpperCase()}
                            </Avatar>
                        </ListItemIcon>
                        <ListItemText
                            primary={currentUser.email.split('@')[0]}
                            secondary="Çıkış Yap"
                            primaryTypographyProps={{ variant: 'body2', fontWeight: 'bold' }}
                            secondaryTypographyProps={{ variant: 'caption' }}
                        />
                    </ListItemButton>
                )}
                <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose} anchorOrigin={{ vertical: 'top', horizontal: 'center' }} transformOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
                    <MenuItem onClick={() => { handleLogout(); handleMenuClose(); }}>Oturumu Kapat</MenuItem>
                </Menu>

                <Typography variant="caption" display="block" color="text.secondary" textAlign="center" sx={{ mt: 2 }}>
                    Bu yazılım Digitalpedi tarafından geliştirilmiştir © 2026
                </Typography>
            </Box>
        </Box>
    );

    return (
        <Box sx={{ display: { lg: 'flex' }, minHeight: '100vh', bgcolor: 'background.default', backgroundImage: 'url(/glass-bg-mesh.png)', backgroundSize: 'cover', backgroundAttachment: 'fixed' }}>
            <AppBar position="fixed" sx={{ display: { lg: 'none' } }}>
                <Toolbar>
                    <IconButton color="inherit" aria-label="open drawer" edge="start" onClick={handleDrawerToggle} sx={{ mr: 2 }}>
                        <MenuIcon />
                    </IconButton>
                    <RouterLink to="/dashboard" style={{ flexGrow: 1, textDecoration: 'none', color: 'inherit', textAlign: 'center' }}>
                        <img src="/reslife-logo.png" alt="ResLife Logo" style={{ height: 30, display: 'block', margin: 'auto' }} />
                    </RouterLink>
                    <NotificationBell />
                </Toolbar>
            </AppBar>

            <Drawer
                variant={isMobile ? "temporary" : "permanent"}
                open={isMobile ? mobileOpen : true}
                onClose={handleDrawerToggle}
                sx={{
                    width: drawerWidth,
                    flexShrink: 0,
                    '& .MuiDrawer-paper': {
                        width: drawerWidth,
                        boxSizing: 'border-box',
                        height: isMobile ? '100%' : 'calc(100% - 32px)', // Float effect on desktop
                        margin: isMobile ? 0 : '16px', // Float margin
                        borderRadius: isMobile ? 0 : '24px', // Oval edges
                        border: 'none',
                        background: isMobile ? 'background.paper' : 'rgba(255, 255, 255, 0.65)', // More translucent on desktop
                        backdropFilter: 'blur(20px)',
                        boxShadow: '0 8px 32px 0 rgba(31, 38, 135, 0.15)'
                    }
                }}
            >
                {drawerContent}
            </Drawer>

            <Box component="main" sx={{
                flexGrow: 1,
                display: 'flex',
                flexDirection: 'column',
                p: { xs: 2, lg: 3 },
                pt: { xs: '80px', lg: 3 }, // Mobil header yüksekliği kadar boşluk
                pb: { xs: '80px', lg: 3 }, // Mobil alt boşluk
                overflowX: 'hidden',
                minHeight: '100vh',
                width: '100%' // İçeriğin taşmasını önle
            }}>
                {/* Desktop Header Removed per request. Using refined layout. */}
                {/* Faz 8: hafif sayfa-giriş animasyonu (fade + 8px yukarı kayma, 280ms).
                    key={pathname} her gezinmede yeniden tetikler; cache (Faz 7) sayesinde
                    ekstra Firestore okuması olmaz. prefers-reduced-motion'da kapanır. */}
                <Box
                    key={location.pathname}
                    sx={{
                        flexGrow: 1,
                        '@keyframes pageEnter': {
                            from: { opacity: 0, transform: 'translateY(8px)' },
                            to: { opacity: 1, transform: 'translateY(0)' },
                        },
                        animation: 'pageEnter 0.28s ease',
                        '@media (prefers-reduced-motion: reduce)': {
                            animation: 'none',
                        },
                    }}
                >
                    <Outlet />
                </Box>
            </Box>
        </Box>
    );
};

export default MainLayout;